import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL, mfiApi } from "../../api";

const COLORS = {
  aquaBlue: "#11a8a4",
  grassGreen: "#7ed957",
  darkGrey: "#3c3b3f",
  vividPurple: "#9440dd",
  white: "#ffffff",
  lightGrey: "#f3f4f6",
  mediumGrey: "#e5e7eb",
  darkText: "#1f2937",
  mediumText: "#6b7280",
  lightText: "#9ca3af",
  whatsapp: "#25D366",
};

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Backend may return an absolute media URL or a relative path — normalize both
function resolveFileUrl(u) {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return `${BASE_URL}${s.startsWith("/") ? s : `/${s}`}`;
}

// Normalize the many possible row shapes from
// GET packages/<package_id>/smart-reports/ into one table row
function normalizeRow(r) {
  const member = r?.member && typeof r.member === "object" ? r.member : {};
  const family = r?.family && typeof r.family === "object" ? r.family : {};
  const fileUrl = resolveFileUrl(
    r?.report_file || r?.report_pdf || r?.file || r?.file_url || r?.report_url
  );
  const phone =
    r?.mobile ||
    r?.phone ||
    r?.mobile_number ||
    member?.mobile ||
    member?.phone ||
    family?.primary_mobile ||
    null;
  return {
    key: r?.id ?? r?.member_id ?? member?.member_id ?? fileUrl ?? Math.random(),
    memberId: r?.member_id || member?.member_id || "—",
    name: r?.member_name || member?.name || r?.name || "—",
    familyId: r?.family_id || family?.family_id || member?.family_id || "—",
    phone: phone ? String(phone).trim() : null,
    fileUrl,
    createdAt: r?.created_at || r?.uploaded_at || r?.screening_date || null,
  };
}

function fileNameOf(row) {
  const code = String(row.memberId || "member").replace(/\s+/g, "_");
  return `Smart_Report_${code}.pdf`;
}

export default function MfiReportPage() {
  const { clientId: paramClientId, packageId: paramPackageId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Support /coordinator/mfi/report-mfi/:clientId/:packageId and ?client_id=&package=
  const clientId = paramClientId || searchParams.get("client_id") || "";
  const packageId = paramPackageId || searchParams.get("package") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [sendingKey, setSendingKey] = useState(null);
  const [toast, setToast] = useState(null); // { ok: bool, msg: string }

  const loadReports = useCallback(async () => {
    if (!packageId) {
      setError("Missing package ID");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await mfiApi.getPackageSmartReports(packageId);
      setRows((Array.isArray(data) ? data : []).map(normalizeRow));
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load smart reports"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) =>
        [r.memberId, r.name, r.familyId].filter(Boolean).join(" ").toLowerCase().includes(q)
      )
    : rows;

  const handleView = (row) => {
    if (!row.fileUrl) {
      setToast({ ok: false, msg: "Report file not available for this row." });
      return;
    }
    window.open(row.fileUrl, "_blank", "noopener");
  };

  const handleDownload = async (row) => {
    if (!row.fileUrl) {
      setToast({ ok: false, msg: "Report file not available for this row." });
      return;
    }
    setDownloadingKey(row.key);
    try {
      // Fetch as blob so the file actually downloads (cross-origin `download`
      // attribute alone is ignored by browsers). Falls back to opening the URL.
      const res = await fetch(row.fileUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileNameOf(row);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      window.open(row.fileUrl, "_blank", "noopener");
    } finally {
      setDownloadingKey(null);
    }
  };

  // Backend-driven send: POST /api/family-members/<member_id>/smart-report/send-whatsapp/
  const handleWhatsApp = async (row) => {
    if (!row.memberId || row.memberId === "—") {
      setToast({ ok: false, msg: "Member ID missing for this row — cannot send WhatsApp." });
      return;
    }
    setSendingKey(row.key);
    try {
      const res = await mfiApi.sendMemberSmartReportWhatsApp(row.memberId);
      if (res && res.success === false) {
        throw new Error(res.message || res.detail || "Failed to send WhatsApp.");
      }
      setToast({ ok: true, msg: res?.message || `WhatsApp sent for ${row.name} (${row.memberId}).` });
    } catch (e) {
      setToast({
        ok: false,
        msg:
          e?.response?.data?.message ||
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to send WhatsApp.",
      });
    } finally {
      setSendingKey(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: COLORS.lightGrey }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.white, padding: "16px 24px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={() => navigate("/coordinator/mfi")}
            style={{ backgroundColor: COLORS.lightGrey, color: COLORS.darkText, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            MFI Clients
          </button>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: COLORS.darkText, margin: 0 }}>Smart Reports</h1>
          {clientId && packageId && (
            <span style={{ fontSize: 12, backgroundColor: `${COLORS.vividPurple}15`, color: COLORS.vividPurple, padding: "4px 8px", borderRadius: 12, fontWeight: 600 }}>
              {clientId} · Package {packageId}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={loadReports} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13, color: COLORS.darkText }}>
            Refresh
          </button>
        </div>
      </div>

      <div style={{ flex: 1, padding: 24, maxWidth: 1200, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40, backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}` }}>
            <div style={{ width: 36, height: 36, border: `4px solid ${COLORS.mediumGrey}`, borderTop: `4px solid ${COLORS.aquaBlue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <p style={{ marginTop: 12, color: COLORS.mediumText, fontSize: 14 }}>Loading smart reports...</p>
          </div>
        ) : error ? (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 20, textAlign: "center" }}>
            <p style={{ color: "#dc2626", fontSize: 14, margin: 0 }}>{error}</p>
            <button onClick={loadReports} style={{ marginTop: 12, backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Retry</button>
          </div>
        ) : (
          <>
            {/* Search */}
            <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 10, padding: 12, marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by member ID, name, family ID..."
                style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.mediumGrey}`, fontSize: 13, outline: "none" }}
              />
              <span style={{ fontSize: 12, color: COLORS.mediumText, whiteSpace: "nowrap" }}>{filtered.length} report{filtered.length !== 1 ? "s" : ""}</span>
            </div>

            {/* Table */}
            <div style={{ backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}`, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 860 }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.lightGrey, textAlign: "left", color: COLORS.mediumText }}>
                      <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>Member ID</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600 }}>Name</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600 }}>Family ID</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600 }}>Mobile</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600 }}>Created</th>
                      <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ padding: 24, textAlign: "center", color: COLORS.mediumText }}>No smart reports found.</td>
                      </tr>
                    ) : (
                      filtered.map((row) => (
                        <tr key={row.key} style={{ borderTop: `1px solid ${COLORS.mediumGrey}` }}>
                          <td style={{ padding: 12, fontWeight: 600, color: COLORS.vividPurple, whiteSpace: "nowrap" }}>{row.memberId}</td>
                          <td style={{ padding: 12, color: COLORS.darkText, fontWeight: 600 }}>{row.name}</td>
                          <td style={{ padding: 12, color: COLORS.darkText }}>{row.familyId}</td>
                          <td style={{ padding: 12, color: COLORS.darkText, whiteSpace: "nowrap" }}>{row.phone || "—"}</td>
                          <td style={{ padding: 12, fontSize: 11, color: COLORS.mediumText, whiteSpace: "nowrap" }}>{formatDate(row.createdAt)}</td>
                          <td style={{ padding: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                              <button
                                onClick={() => handleView(row)}
                                disabled={!row.fileUrl}
                                title="View report"
                                style={{ backgroundColor: !row.fileUrl ? COLORS.mediumGrey : COLORS.darkGrey, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 12px", cursor: !row.fileUrl ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDownload(row)}
                                disabled={!row.fileUrl || downloadingKey === row.key}
                                title="Download PDF"
                                style={{ backgroundColor: !row.fileUrl ? COLORS.mediumGrey : COLORS.aquaBlue, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 12px", cursor: !row.fileUrl ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                              >
                                {downloadingKey === row.key ? "Downloading…" : "Download"}
                              </button>
                              <button
                                onClick={() => handleWhatsApp(row)}
                                disabled={sendingKey === row.key || !row.memberId || row.memberId === "—"}
                                title="Send smart report on WhatsApp"
                                style={{ backgroundColor: !row.memberId || row.memberId === "—" ? COLORS.mediumGrey : COLORS.whatsapp, color: COLORS.white, border: "none", borderRadius: 6, padding: "7px 12px", cursor: sendingKey === row.key ? "wait" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                              >
                                {sendingKey === row.key ? "Sending…" : "WhatsApp"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Toast (success / error) */}
      {toast && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 3000, background: toast.ok ? "#ecfdf5" : "#fffbeb", border: toast.ok ? "1px solid #a7f3d0" : "1px solid #fcd34d", borderRadius: 10, padding: "10px 12px", maxWidth: 360, boxShadow: "0 6px 20px rgba(0,0,0,0.15)", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16 }}>{toast.ok ? "✓" : "⚠️"}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: toast.ok ? "#065f46" : "#92400e", marginTop: 2 }}>{toast.msg}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <button
                onClick={() => setToast(null)}
                style={{ background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
